import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';
import { useAuth } from '../../context/AuthContext';
import { moduleDescription, moduleLabel } from '../../utils/moduleAccess';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import PlanPricingCard from './PlanPricingCard';
import BillingCycleComparison from './BillingCycleComparison';

export default function ModuleUpgradeDialog({
  open,
  onClose,
  moduleKey,
  moduleLabel: labelOverride,
}) {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const [pendingPlan, setPendingPlan] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const displayLabel = labelOverride || data?.module_label || moduleLabel(moduleKey);
  const displayDescription = data?.module_description || moduleDescription(moduleKey);

  useEffect(() => {
    if (!open || !moduleKey) return undefined;
    setLoading(true);
    setData(null);
    setPendingPlan(null);
    setConfirmPlan(null);
    setPreview(null);
    api.get('subscription/plans/for-module', { params: { module: moduleKey } })
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    return undefined;
  }, [open, moduleKey]);

  const plans = data?.plans || [];
  const currentPlanName = data?.current_plan?.name || user?.subscription?.plan_name || 'Current Plan';

  const openBillingForPlan = (plan) => {
    setPendingPlan(plan);
    setConfirmPlan(null);
    setPreview(null);
  };

  const handleCycleSelect = async (cycle) => {
    if (!pendingPlan) return;
    setSelectedCycle(cycle);
    setConfirmPlan({ plan: pendingPlan, cycle });
    setPendingPlan(null);
    try {
      const res = await api.post('subscription/upgrade/preview', {
        plan_id: pendingPlan.id,
        billing_cycle: cycle,
      });
      setPreview(res.data?.data || null);
    } catch {
      setPreview(null);
    }
  };

  const checkout = async () => {
    if (!confirmPlan) return;
    setCheckingOut(true);
    try {
      const { plan, cycle } = confirmPlan;
      const res = await api.post('subscription/checkout', {
        plan_id: plan.id,
        billing_cycle: cycle,
        action: user?.subscription?.plan_id ? 'upgrade' : 'subscribe',
        auto_renew: false,
      });
      const checkoutData = res.data?.data;
      if (checkoutData?.activated) {
        await refreshUser?.();
        toast({ title: 'Plan activated', description: `${displayLabel} is now available.` });
        onClose?.();
        return;
      }
      if (!checkoutData?.order_id || !checkoutData?.key_id) {
        toast({ title: 'Error', description: 'Could not start checkout', variant: 'destructive' });
        return;
      }
      await openRazorpayCheckout(checkoutData, {
        name: user?.name || user?.full_name || '',
        email: user?.email || '',
        phone: user?.phone || user?.mobile || '',
        onSuccess: async (payload) => {
          await api.post('subscription/payments/verify', payload);
          await refreshUser?.();
          toast({ title: 'Payment successful', description: `${displayLabel} is now unlocked.` });
          onClose?.();
        },
      });
    } catch (e) {
      if (e?.message !== 'Payment cancelled') {
        toast({
          title: 'Error',
          description: e.response?.data?.message || e.message || 'Checkout failed',
          variant: 'destructive',
        });
      }
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{displayLabel}</DialogTitle>
            <DialogDescription>{displayDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
              <p className="font-medium">Current plan: {currentPlanName}</p>
              <p className="text-muted-foreground mt-1">
                {data?.message || 'This feature is not available in your current subscription.'}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Available plans</h3>
              {loading && <p className="text-sm text-muted-foreground">Loading plans…</p>}
              {!loading && plans.length === 0 && (
                <p className="text-sm text-muted-foreground">No upgrade plans available for this module.</p>
              )}
              {!loading && plans.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2">
                  {plans.map((plan) => (
                    <PlanPricingCard
                      key={plan.id}
                      plan={plan}
                      cycle={selectedCycle}
                      actionLabel="Upgrade"
                      onSelect={() => openBillingForPlan(plan)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pendingPlan)} onOpenChange={(v) => { if (!v) setPendingPlan(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {pendingPlan && (
            <BillingCycleComparison
              plan={pendingPlan}
              action={user?.subscription?.plan_id ? 'upgrade' : 'subscribe'}
              onSelectCycle={handleCycleSelect}
              onCancel={() => setPendingPlan(null)}
              showHeader
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmPlan)} onOpenChange={(v) => { if (!v) { setConfirmPlan(null); setPreview(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm upgrade</DialogTitle>
            <DialogDescription>
              Upgrade to {confirmPlan?.plan?.name} ({confirmPlan?.cycle?.replace(/_/g, ' ')}) to unlock {displayLabel}.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="text-sm space-y-1">
              <p>Plan price: ₹{preview.new_plan_price ?? preview.amount}</p>
              {preview.credit > 0 && <p>Credit from current plan: ₹{preview.credit}</p>}
              <p className="font-semibold">Amount due: ₹{preview.payable}</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setConfirmPlan(null); setPreview(null); }}>Cancel</Button>
            <Button className="bg-blue-600" onClick={checkout} disabled={checkingOut}>
              {checkingOut ? 'Processing…' : 'Proceed to payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
